import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCpu, FiExternalLink, FiX, FiCheck, FiCopy, FiBookmark, FiLoader, FiTag, FiKey } = FiIcons;

export default function AISummaryModal({
  isOpen,
  onClose,
  bookmark,
  item,
  onSaveToNote,
  onSaveNote,
  accentColor = '#c2ffe0',
  themeColors: tc = {},
  isDark: isDarkProp,
  onOpenSettings,
}) {
  const targetBookmark = bookmark || item;
  const saveHandler = onSaveToNote || onSaveNote;

  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !tc?.card?.toLowerCase().includes('ffffff') && !tc?.card?.startsWith('rgba(255');

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState([]);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && targetBookmark) {
      setSummary('');
      setTags([]);
      setSaved(false);
      setCopied(false);
      setError('');
      generateSummary(targetBookmark);
    }
  }, [isOpen, targetBookmark]);

  async function generateSummary(bm) {
    const userByokKey = localStorage.getItem('memark_byok_api_key') || '';
    if (!userByokKey) {
      setLoading(false);
      setError('KEY_REQUIRED');
      return;
    }

    setLoading(true);
    setError('');

    const prompt = `You are a web intelligence assistant.
Analyze this bookmarked website:
Title: "${bm.title}"
URL: "${bm.url}"
Existing Notes: "${bm.note || 'None'}"

Provide a concise, helpful breakdown in this exact JSON format (NO extra text, no markdown outside json):
{
  "summary": "2 clear sentences explaining what this service or tool does and its primary value.",
  "tags": ["Tag1", "Tag2", "Tag3", "Tag4"]
}`;

    const userModel = localStorage.getItem('memark_ai_model') || 'openrouter/free';

    const bodyPayload = {
      model: userModel,
      messages: [
        { role: 'system', content: 'You are an accurate web analysis engine. Always output pure valid JSON.' },
        { role: 'user', content: prompt },
      ],
      temperature: 0.2,
      max_tokens: 450,
    };

    try {
      let res = null;

      // 1. Direct OpenRouter call with user's personal BYOK key
      try {
        res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${userByokKey}`,
            'HTTP-Referer': window.location.origin,
            'X-Title': 'MeMark BYOK AI',
          },
          body: JSON.stringify(bodyPayload),
        });
      } catch (_) {
        res = null;
      }

      // 2. Reverse proxy fallback with personal BYOK token
      if (!res || !res.ok) {
        try {
          res = await fetch('/api/ai/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${userByokKey}`,
              'HTTP-Referer': window.location.origin,
              'X-Title': 'MeMark AI Dashboard',
            },
            body: JSON.stringify(bodyPayload),
          });
          if (res.status === 404) res = null;
        } catch (_) {
          res = null;
        }
      }

      if (!res || !res.ok) {
        throw new Error(`HTTP ${res?.status || 500}: Could not reach OpenRouter with configured key`);
      }

      const json = await res.json();
      let content = json?.choices?.[0]?.message?.content || '';
      content = content.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();

      // Extract JSON
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        setSummary(parsed.summary || 'Summary generated.');
        setTags(parsed.tags || []);
      } else {
        setSummary(content || 'No summary could be parsed.');
      }
    } catch (err) {
      console.error('AI summary failed:', err);
      // Fallback sensible summary if AI network fails
      try {
        const domain = new URL(bm.url).hostname.replace(/^www\./, '');
        setSummary(`${bm.title} is hosted on ${domain}. Direct web access link for personal reference.`);
        setTags([domain.split('.')[0], 'Bookmark']);
      } catch {
        setError('Could not generate AI summary at this moment.');
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSaveToNote() {
    if (!summary || !targetBookmark || !saveHandler) return;
    const combinedTags = tags.length > 0 ? ` [${tags.join(', ')}]` : '';
    const newNote = `${summary}${combinedTags}`;
    saveHandler(targetBookmark.id, newNote);
    setSaved(true);
    setTimeout(() => onClose(), 1200);
  }

  function handleCopy() {
    if (!summary) return;
    navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!isOpen || !targetBookmark) return null;

  const currentAccent = tc.accent || accentColor;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.2 }}
          className={`relative w-full max-w-md rounded-2xl border shadow-2xl p-6 overflow-hidden z-10 ${
            isDark ? 'bg-[#141418] border-[#26262e]' : 'bg-white border-slate-200'
          }`}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: `${currentAccent}22`,
                  border: `1px solid ${currentAccent}44`,
                  color: currentAccent,
                }}
              >
                <SafeIcon icon={FiCpu} className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  AI Bookmark Summary
                </h3>
                <p className={`text-xs truncate max-w-[240px] ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {targetBookmark.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                isDark ? 'text-zinc-400 hover:text-white hover:bg-zinc-800' : 'text-slate-400 hover:text-slate-800 hover:bg-slate-100'
              }`}
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Website Link Pill */}
          <div
            className={`mb-4 px-3 py-2 rounded-xl border flex items-center justify-between text-xs ${
              isDark ? 'bg-[#0e0e11] border-[#202026] text-zinc-400' : 'bg-slate-50 border-slate-200 text-slate-600'
            }`}
          >
            <span className={`truncate max-w-[280px] font-mono text-[11px] ${isDark ? 'text-zinc-300' : 'text-slate-700'}`}>
              {targetBookmark.url}
            </span>
            <a
              href={targetBookmark.url}
              target="_blank"
              rel="noreferrer"
              className="hover:underline flex items-center gap-1 shrink-0 ml-2 font-medium"
              style={{ color: currentAccent }}
            >
              <span>Visit</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
            </a>
          </div>

          {/* Body */}
          <div className="min-h-[110px] mb-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-8 text-center text-zinc-400">
                <SafeIcon icon={FiLoader} className="w-6 h-6 animate-spin mb-3" style={{ color: currentAccent }} />
                <p className="text-xs">Analyzing website content via AI...</p>
              </div>
            ) : error === 'KEY_REQUIRED' ? (
              <div className="py-5 px-4 text-center rounded-2xl border space-y-3 bg-amber-500/10 border-amber-500/30 text-amber-300">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto text-amber-400">
                  <SafeIcon icon={FiKey} className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-amber-200">Personal OpenRouter Key Required</h4>
                  <p className="text-[11px] text-amber-300/80 mt-1 leading-relaxed">
                    To generate automated AI summaries and tags, please input your personal key in Settings → AI Gateway.
                    Your key is saved locally in your browser.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenSettings?.('ai');
                  }}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-black transition-all shadow-sm cursor-pointer"
                >
                  <SafeIcon icon={FiKey} className="w-3.5 h-3.5" />
                  <span>Configure Key in Settings →</span>
                </button>
              </div>
            ) : error ? (
              <div className="py-6 text-center text-xs text-red-400 bg-red-500/10 rounded-xl p-3 border border-red-500/20">
                {error}
              </div>
            ) : (
              <div className="space-y-3">
                <p
                  className={`text-sm leading-relaxed p-4 rounded-xl border ${
                    isDark ? 'bg-[#191920] border-[#2a2a34] text-zinc-200' : 'bg-slate-50 border-slate-200 text-slate-800'
                  }`}
                >
                  {summary}
                </p>

                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {tags.map((tag, i) => (
                      <span
                        key={i}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${
                          isDark
                            ? 'bg-[#1e1e26] border-[#2d2d38]'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                        }`}
                        style={{ color: isDark ? currentAccent : undefined }}
                      >
                        <SafeIcon icon={FiTag} className="w-2.5 h-2.5" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={`flex items-center justify-end gap-2 pt-2 border-t ${isDark ? 'border-[#222228]' : 'border-slate-200'}`}>
            <button
              onClick={handleCopy}
              disabled={loading || !summary}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer ${
                isDark
                  ? 'text-zinc-300 hover:text-white bg-[#1e1e24] hover:bg-[#282830]'
                  : 'text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200'
              }`}
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>

            <button
              onClick={handleSaveToNote}
              disabled={loading || !summary || saved}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-black transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-md cursor-pointer"
              style={{
                backgroundColor: currentAccent,
              }}
            >
              <SafeIcon icon={saved ? FiCheck : FiBookmark} className="w-3.5 h-3.5" />
              <span>{saved ? 'Saved to Notes!' : 'Save to Bookmark Notes'}</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
